import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { required } from "../../../utils/validators";
import { useFormValidation } from "../../../utils/useFormValidation";
import { ModalShell } from "../ModalShell";
import { ModalActions } from "../ModalActions";
import { NAV_ICONS } from "../../../components/icons/navIcons";
import clsx from "clsx";
import type { Jurisdiction } from "../../../types/jurisdiction.types";
import { getApiErrorMessage } from "../../../utils/apiError";

interface JurisdictionModalProps {
  value?: Jurisdiction;
  onClose: () => void;
  onSave: (data: { id?: string; jurisdiction: string }) => Promise<void>;
  mode?: "add" | "edit" | "view";
}

export function JurisdictionModal({
  value,
  onClose,
  onSave,
  mode = "add",
}: JurisdictionModalProps) {
  const { t } = useTranslation("jurisdictions");
  const isView = mode === "view";
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const refs = {
    jurisdiction: useRef<HTMLInputElement>(null),
  };

  const [form, setForm] = useState({
    id: value?.id || "",
    jurisdiction: value?.jurisdictionName || "",
  });

  const { errors, isValid, markAllTouched, shouldShowError, focusFirstError } =
    useFormValidation(form, {
      jurisdiction: [required],
    });

  const handleSave = async () => {
    if (!isValid) {
      markAllTouched();
      focusFirstError(refs);
      return;
    }

    try {
      setSaving(true);
      setApiError(null);

      await onSave(form);
    } catch (e: any) {
      setApiError(getApiErrorMessage(e, t("jurisdiction.messages.saveFailedMessage", {
            defaultValue: "Failed to save jurisdiction",
          })));
    } finally {
      setSaving(false);
    }
  };

  const labelClass =
    "block text-sm font-medium mb-1 text-[rgb(var(--text-primary))]";
  const inputClass =
    "input w-full text-[rgb(var(--text-primary))] transition-all";

  return (
    <ModalShell
      title={
        <div className="flex items-center gap-2">
          <NAV_ICONS.organisation size={18} />
          <span className="font-medium">
            {isView
              ? t("jurisdiction.modalTitle.view", {
                  defaultValue: "View Jurisdiction",
                })
              : value
                ? t("jurisdiction.modalTitle.edit", {
                    defaultValue: "Edit Jurisdiction",
                  })
                : t("jurisdiction.modalTitle.add", {
                    defaultValue: "Add Jurisdiction",
                  })}
          </span>
        </div>
      }
      onClose={onClose}
    >
      <div className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-5">
          {/* SECTION HEADER */}
          <div>
            <h3 className="font-semibold text-sm border-b pb-1 text-[rgb(var(--text-accent))]">
              {t("jurisdiction.sectionHeaders.generalInformation", {
                defaultValue: "General Information",
              })}
            </h3>
          </div>

          {/* JURISDICTION INPUT */}
          <div>
            <label className={labelClass}>
              {t("jurisdiction.labels.type", {
                defaultValue: "Jurisdiction",
              })}{" "}
              <span className="text-red-600">*</span>
            </label>
            <input
              ref={refs.jurisdiction}
              type="text"
              disabled={isView}
              placeholder={t("jurisdiction.placeholders.typeExample", {
                defaultValue: "e.g. India, England and Wales",
              })}
              aria-invalid={(shouldShowError("jurisdiction") && errors.jurisdiction) ? true : undefined}
              className={clsx(
                inputClass,
                shouldShowError("jurisdiction") &&
                  errors.jurisdiction &&
                  "input-invalid",
              )}
              value={form.jurisdiction}
              onChange={(e) =>
                setForm({ ...form, jurisdiction: e.target.value })
              }
            />
            {shouldShowError("jurisdiction") && errors.jurisdiction && (
              <p className="text-xs text-red-600 mt-1">{errors.jurisdiction}</p>
            )}
          </div>
        </div>

        {apiError && (
          <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm font-medium">
            {apiError}
          </div>
        )}
      </div>

      {!isView && (
        <ModalActions
          onClose={onClose}
          onSave={handleSave}
          disabled={saving}
          loading={saving}
        />
      )}
    </ModalShell>
  );
}
